import React, { useState, useEffect, useCallback } from 'react';
import './TicTacToe.css'; // We'll add a little external CSS for polish

// Square component for individual cells on the board
function Square({ value, onClick, isWinningSquare }) {
  return (
    <button
      className={`square ${isWinningSquare ? 'winning' : ''} ${value === 'X' ? 'x' : 'o'}`}
      onClick={onClick}
    >
      {value}
    </button>
  );
}

// Helper function to calculate the winner
function calculateWinner(squares) {
  const lines = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8],
    [0, 3, 6], [1, 4, 7], [2, 5, 8],
    [0, 4, 8], [2, 4, 6],
  ];
  for (let [a, b, c] of lines) {
    if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
      return { winner: squares[a], line: [a, b, c] };
    }
  }
  return null;
}

// Minimax algorithm for AI
function minimax(board, depth, isMaximizing) {
  const winnerInfo = calculateWinner(board);
  if (winnerInfo) {
    if (winnerInfo.winner === 'O') return 10 - depth;
    if (winnerInfo.winner === 'X') return depth - 10;
  }
  if (!board.includes(null)) return 0;

  if (isMaximizing) {
    let bestScore = -Infinity;
    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        board[i] = 'O';
        bestScore = Math.max(bestScore, minimax(board, depth + 1, false));
        board[i] = null;
      }
    }
    return bestScore;
  } else {
    let bestScore = Infinity;
    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        board[i] = 'X';
        bestScore = Math.min(bestScore, minimax(board, depth + 1, true));
        board[i] = null;
      }
    }
    return bestScore;
  }
}

function findHardAIMove(board) {
  let bestScore = -Infinity, move = -1;
  for (let i = 0; i < 9; i++) {
    if (board[i] === null) {
      board[i] = 'O';
      let score = minimax(board, 0, false);
      board[i] = null;
      if (score > bestScore) {
        bestScore = score;
        move = i;
      }
    }
  }
  return move;
}

function findEasyAIMove(board) {
  const moves = board.map((v, i) => v === null ? i : null).filter(v => v !== null);
  return moves.length > 0 ? moves[Math.floor(Math.random() * moves.length)] : -1;
}

// Main Component
function TicTacToe() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [xIsNext, setXIsNext] = useState(true);
  const [gameMode, setGameMode] = useState('humanVsAI');
  const [aiDifficulty, setAiDifficulty] = useState('hard');
  const [winningLine, setWinningLine] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [status, setStatus] = useState('');
  const [scoreX, setScoreX] = useState(0);
  const [scoreO, setScoreO] = useState(0);
  const [draws, setDraws] = useState(0);

  useEffect(() => {
    const winnerInfo = calculateWinner(board);
    if (winnerInfo) {
      setStatus(`${winnerInfo.winner} Wins!`);
      setWinningLine(winnerInfo.line);
      setGameOver(true);
      winnerInfo.winner === 'X' ? setScoreX(prev => prev + 1) : setScoreO(prev => prev + 1);
    } else if (!board.includes(null)) {
      setStatus('Draw!');
      setGameOver(true);
      setDraws(prev => prev + 1);
    } else {
      setStatus(`${xIsNext ? 'X' : 'O'} Turn`);
    }
  }, [board, xIsNext]);

  useEffect(() => {
    if (gameMode === 'humanVsAI' && !xIsNext && !gameOver) {
      const timer = setTimeout(() => {
        const move = aiDifficulty === 'hard' ? findHardAIMove([...board]) : findEasyAIMove([...board]);
        if (move !== -1) handleClick(move);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [xIsNext, board, gameOver, gameMode, aiDifficulty]);

  const handleClick = (i) => {
    if (board[i] || gameOver) return;
    const copy = [...board];
    copy[i] = xIsNext ? 'X' : 'O';
    setBoard(copy);
    setXIsNext(!xIsNext);
  };

  const resetBoard = () => {
    setBoard(Array(9).fill(null));
    setXIsNext(true);
    setWinningLine(null);
    setGameOver(false);
  };

  const newGame = () => {
    setScoreX(0);
    setScoreO(0);
    setDraws(0);
    resetBoard();
  };

  return (
    <div className="tictoc">
      <h1 className="title">🎯 Tic Tac Toe 🎮</h1>

      <div className="mode-select">
        <button onClick={() => { setGameMode('humanVsHuman'); newGame(); }} className={gameMode === 'humanVsHuman' ? 'active' : ''}>👥 Human vs Human</button>
        <button onClick={() => { setGameMode('humanVsAI'); newGame(); }} className={gameMode === 'humanVsAI' ? 'active' : ''}>🤖 Human vs AI</button>
      </div>

      {gameMode === 'humanVsAI' && (
        <div className="difficulty-select">
          <button onClick={() => { setAiDifficulty('easy'); newGame(); }} className={aiDifficulty === 'easy' ? 'easy' : ''}>😄 Easy</button>
          <button onClick={() => { setAiDifficulty('hard'); newGame(); }} className={aiDifficulty === 'hard' ? 'hard' : ''}>🔥 Hard</button>
        </div>
      )}

      <div className="board">
        {board.map((value, i) => (
          <Square key={i} value={value} onClick={() => handleClick(i)} isWinningSquare={winningLine?.includes(i)} />
        ))}
      </div>

      <div className="status">{status}</div>

      <div className="scores">
        <div>❌ X: {scoreX}</div>
        <div>🤝 Draw: {draws}</div>
        <div>⭕ O: {scoreO}</div>
      </div>

      <div className="controls">
        {gameOver ? (
          <button onClick={resetBoard} className="play-again">Play Again 🔄</button>
        ) : (
          <button onClick={newGame} className="new-game">New Game 🆕</button>
        )}
      </div>
    </div>
  );
}

export default TicTacToe;





// import React, { useState, useEffect, useCallback, useRef } from 'react';
// import io from 'socket.io-client';
// import './TicTacToe.css';
// import baseUrl from '../../config';

// function Square({ value, onClick, isWinningSquare }) {
//   return (
//     <button
//       className={`square ${isWinningSquare ? 'winning' : ''} ${value === 'X' ? 'x' : 'o'}`}
//       onClick={onClick}
//     >
//       {value}
//     </button>
//   );
// }

// function TicTacToe() {
//   const [board, setBoard] = useState(Array(9).fill(null));
//   const [xIsNext, setXIsNext] = useState(true);
//   const [gameMode, setGameMode] = useState('humanVsAI');
//   const [aiDifficulty, setAiDifficulty] = useState('hard');
//   const [winningLine, setWinningLine] = useState(null);
//   const [gameOver, setGameOver] = useState(false);
//   const [status, setStatus] = useState('');
//   const [scoreX, setScoreX] = useState(0);
//   const [scoreO, setScoreO] = useState(0);
//   const [draws, setDraws] = useState(0);
//   const [invitations, setInvitations] = useState([]);
//   const [activeGameRoom, setActiveGameRoom] = useState(null);
//   const [opponentName, setOpponentName] = useState('');
//   const [opponentId, setOpponentId] = useState(null);
//   const [isMyTurn, setIsMyTurn] = useState(false);
//   const [playerSymbol, setPlayerSymbol] = useState(null);
//   const [waitingForOpponent, setWaitingForOpponent] = useState(false);
//   const [currentUserId, setCurrentUserId] = useState(null);
//   const [currentUserRole, setCurrentUserRole] = useState(null);
//   const [currentUserName, setCurrentUserName] = useState(null);
//   const [connectionStatus, setConnectionStatus] = useState('disconnected');

//   const socket = useRef(null);

//   // Load user data
//   useEffect(() => {
//     const userString = localStorage.getItem('user');
//     if (userString) {
//       try {
//         const user = JSON.parse(userString);
//         setCurrentUserId(user.id);
//         setCurrentUserRole(user.role);
//         setCurrentUserName(user.name);
//       } catch (e) {
//         console.error("Failed to parse user data:", e);
//       }
//     }
//   }, []);

//   // Socket connection and event handling
//   useEffect(() => {
//     if (!currentUserId || !currentUserRole) return;

//     const connectSocket = () => {
//       if (socket.current?.connected) return;

//       socket.current = io(`${baseUrl}game`, {
//         auth: { token: localStorage.getItem('token') },
//         transports: ['websocket'],
//         reconnection: true,
//         reconnectionAttempts: Infinity,
//         reconnectionDelay: 1000,
//         reconnectionDelayMax: 5000,
//         timeout: 20000,
//         pingTimeout: 25000,
//         pingInterval: 10000
//       });

//       socket.current.on('connect', () => {
//         console.log('Connected to game server');
//         setConnectionStatus('connected');
//         setStatus('Connected to game server');
//         if (currentUserRole === 'student') {
//           fetchInvitations();
//         }
//         if (activeGameRoom) {
//           socket.current.emit('syncGameState');
//         }
//       });

//       socket.current.on('disconnect', (reason) => {
//         console.log('Disconnected:', reason);
//         setConnectionStatus('disconnected');
//         if (reason === 'io server disconnect') {
//           setTimeout(() => socket.current.connect(), 1000);
//         }
//       });

//       socket.current.on('connect_error', (err) => {
//         console.error('Connection error:', err);
//         setConnectionStatus('reconnecting');
//         setStatus('Connection error. Reconnecting...');
//       });

//       socket.current.on('reconnect', (attempt) => {
//         console.log(`Reconnected after ${attempt} attempts`);
//         setConnectionStatus('connected');
//         setStatus('Reconnected to game server');
//         if (currentUserRole === 'student') {
//           fetchInvitations();
//         }
//         if (activeGameRoom) {
//           socket.current.emit('syncGameState');
//         }
//       });

//       socket.current.on('reconnect_failed', () => {
//         console.error('Reconnection failed');
//         setConnectionStatus('disconnected');
//         setStatus('Failed to reconnect. Please refresh the page.');
//       });

//       // Game event handlers
//       socket.current.on('newInvitation', (invitation) => {
//         setInvitations(prev => {
//           if (!prev.some(inv => inv._id === invitation.invitationId)) {
//             return [...prev, {
//               _id: invitation.invitationId,
//               teacher: invitation.from,
//               status: 'pending'
//             }];
//           }
//           return prev;
//         });
//         setStatus(`New invitation from ${invitation.from.name}!`);
//       });

//       socket.current.on('invitationAcceptedConfirmation', ({ invitationId, teacherId, teacherName }) => {
//         setPlayerSymbol('O');
//         setOpponentName(teacherName);
//         setOpponentId(teacherId);
//         setWaitingForOpponent(true);
//         setStatus(`You accepted ${teacherName}'s invitation. Starting game...`);
//         setInvitations(prev => prev.filter(inv => inv._id !== invitationId));
//         setGameMode('studentVsTeacher');
//       });

//       socket.current.on('gameStarted', ({ roomId, board: initialBoard, currentPlayer, opponentName, opponentId }) => {
//         setActiveGameRoom(roomId);
//         setBoard(initialBoard);
//         setOpponentName(opponentName);
//         setOpponentId(opponentId);
//         setGameOver(false);
//         setWinningLine(null);
//         setIsMyTurn(currentPlayer === 'student');
//         setWaitingForOpponent(false);
//         setStatus(currentPlayer === 'student' ? 'Your Turn!' : `${opponentName}'s Turn`);
//       });

//       socket.current.on('moveMade', ({ board: newBoard, currentPlayer }) => {
//         setBoard(newBoard);
//         setIsMyTurn(currentPlayer === 'student');
//         setStatus(currentPlayer === 'student' ? 'Your Turn!' : `${opponentName}'s Turn`);
//       });

//       socket.current.on('gameOver', ({ winner, winningLine, board: finalBoard }) => {
//         setBoard(finalBoard);
//         setWinningLine(winningLine);
//         setGameOver(true);
        
//         if (winner === currentUserId) {
//           setStatus('You Win!');
//           setScoreO(prev => prev + 1);
//         } else if (winner) {
//           setStatus(`${opponentName} Wins!`);
//           setScoreX(prev => prev + 1);
//         } else {
//           setStatus('Draw!');
//           setDraws(prev => prev + 1);
//         }
//       });

//       socket.current.on('opponentLeft', ({ roomId }) => {
//         if (roomId === activeGameRoom) {
//           setStatus(`${opponentName} has left the game`);
//           setActiveGameRoom(null);
//           setOpponentName('');
//           setOpponentId(null);
//           setGameOver(true);
//           setIsMyTurn(false);
//         }
//       });

//       socket.current.on('gameStateUpdate', ({ roomId, board, currentPlayer, status, winner, winningLine }) => {
//         if (roomId === activeGameRoom) {
//           setBoard(board);
//           setWinningLine(winningLine);
//           setIsMyTurn(currentPlayer === 'student');
//           if (status === 'completed') {
//             setGameOver(true);
//             if (winner === currentUserId) {
//               setStatus('You Win!');
//             } else if (winner) {
//               setStatus(`${opponentName} Wins!`);
//             } else {
//               setStatus('Draw!');
//             }
//           } else {
//             setStatus(currentPlayer === 'student' ? 'Your Turn!' : `${opponentName}'s Turn`);
//           }
//         }
//       });
//     };

//     connectSocket();

//     return () => {
//       if (socket.current) {
//         socket.current.off('connect');
//         socket.current.off('disconnect');
//         socket.current.off('connect_error');
//         socket.current.off('reconnect');
//         socket.current.off('reconnect_failed');
//         // Remove all game event listeners
//         socket.current.off('newInvitation');
//         socket.current.off('invitationAcceptedConfirmation');
//         socket.current.off('gameStarted');
//         socket.current.off('moveMade');
//         socket.current.off('gameOver');
//         socket.current.off('opponentLeft');
//         socket.current.off('gameStateUpdate');
        
//         if (!activeGameRoom) {
//           socket.current.disconnect();
//         }
//       }
//     };
//   }, [currentUserId, currentUserRole, activeGameRoom, opponentName]);

//   // Game logic for local modes
//   useEffect(() => {
//     if (gameMode !== 'studentVsTeacher') {
//       const winnerInfo = calculateWinner(board);
//       if (winnerInfo) {
//         setStatus(`${winnerInfo.winner} Wins!`);
//         setWinningLine(winnerInfo.line);
//         setGameOver(true);
//         winnerInfo.winner === 'X' ? setScoreX(prev => prev + 1) : setScoreO(prev => prev + 1);
//       } else if (!board.includes(null)) {
//         setStatus('Draw!');
//         setGameOver(true);
//         setDraws(prev => prev + 1);
//       } else {
//         setStatus(`${xIsNext ? 'X' : 'O'} Turn`);
//       }
//     }
//   }, [board, xIsNext, gameMode]);

//   // AI move logic
//   useEffect(() => {
//     if (gameMode === 'humanVsAI' && !xIsNext && !gameOver) {
//       const timer = setTimeout(() => {
//         const move = aiDifficulty === 'hard' ? findHardAIMove([...board]) : findEasyAIMove([...board]);
//         if (move !== -1) {
//           const newBoard = [...board];
//           newBoard[move] = 'O';
//           setBoard(newBoard);
//           setXIsNext(true);
//         }
//       }, 400);
//       return () => clearTimeout(timer);
//     }
//   }, [xIsNext, board, gameOver, gameMode, aiDifficulty]);

//   const handleClick = (i) => {
//     if (board[i] || gameOver) return;

//     if (gameMode === 'studentVsTeacher') {
//       if (!activeGameRoom || !isMyTurn) {
//         if (!isMyTurn) setStatus("It's not your turn!");
//         return;
//       }
//       if (socket.current?.connected) {
//         socket.current.emit('makeMove', { roomId: activeGameRoom, position: i });
//         setIsMyTurn(false);
//       } else {
//         setStatus('Connection lost. Reconnecting...');
//       }
//     } else {
//       const newBoard = [...board];
//       newBoard[i] = xIsNext ? 'X' : 'O';
//       setBoard(newBoard);
//       setXIsNext(!xIsNext);
//     }
//   };

//   const resetBoard = () => {
//     setBoard(Array(9).fill(null));
//     setWinningLine(null);
//     setGameOver(false);
//     setXIsNext(true);

//     if (gameMode === 'studentVsTeacher' && activeGameRoom && socket.current?.connected) {
//       socket.current.emit('rematchRequest', { roomId: activeGameRoom });
//       setStatus(`Requesting rematch with ${opponentName}...`);
//       setIsMyTurn(false);
//     } else {
//       setActiveGameRoom(null);
//       setOpponentName('');
//       setOpponentId(null);
//       setIsMyTurn(false);
//       setPlayerSymbol(null);
//       setWaitingForOpponent(false);
//       setStatus('Board reset. Ready for new game.');
//     }
//   };

//   const newGame = () => {
//     setScoreX(0);
//     setScoreO(0);
//     setDraws(0);
//     setBoard(Array(9).fill(null));
//     setWinningLine(null);
//     setGameOver(false);
//     setXIsNext(true);

//     if (gameMode === 'studentVsTeacher' && socket.current?.connected && activeGameRoom) {
//       socket.current.emit('leaveGame', { roomId: activeGameRoom });
//     }

//     setInvitations([]);
//     setActiveGameRoom(null);
//     setOpponentName('');
//     setOpponentId(null);
//     setIsMyTurn(false);
//     setPlayerSymbol(null);
//     setWaitingForOpponent(false);

//     setStatus('New game started. Select a mode!');
//     if (currentUserRole === 'student' && gameMode === 'studentVsTeacher') {
//       fetchInvitations();
//     }
//   };

//   const fetchInvitations = useCallback(async () => {
//     if (currentUserRole !== 'student' || !currentUserId) return;
//     try {
//       const token = localStorage.getItem('token');
//       const response = await fetch(`${baseUrl}api/games/invitations`, {
//         headers: { 'Authorization': `Bearer ${token}` }
//       });
//       if (response.ok) {
//         const data = await response.json();
//         setInvitations(data.filter(inv => inv.status === 'pending' && inv.student === currentUserId));
//       } else {
//         console.error('Failed to fetch invitations:', response.statusText);
//       }
//     } catch (error) {
//       console.error('Error fetching invitations:', error);
//     }
//   }, [currentUserRole, currentUserId]);

//   const handleInvitationResponse = async (invitationId, accept) => {
//     try {
//       const token = localStorage.getItem('token');
//       const response = await fetch(`${baseUrl}api/games/invitations/${invitationId}/respond`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`
//         },
//         body: JSON.stringify({ action: accept ? 'accepted' : 'rejected' })
//       });

//       if (response.ok) {
//         setInvitations(prev => prev.filter(inv => inv._id !== invitationId));
//         if (accept) {
//           setStatus('Invitation accepted. Starting game...');
//           setWaitingForOpponent(true);
//         } else {
//           setStatus('Invitation rejected.');
//         }
//       } else {
//         const errorData = await response.json();
//         setStatus(`Error: ${errorData.message}`);
//       }
//     } catch (error) {
//       console.error('Error responding to invitation:', error);
//       setStatus('Network error responding to invitation');
//     }
//   };

//   // Helper functions for AI moves
//   function calculateWinner(squares) {
//     const lines = [
//       [0, 1, 2], [3, 4, 5], [6, 7, 8],
//       [0, 3, 6], [1, 4, 7], [2, 5, 8],
//       [0, 4, 8], [2, 4, 6]
//     ];
//     for (let [a, b, c] of lines) {
//       if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
//         return { winner: squares[a], line: [a, b, c] };
//       }
//     }
//     return null;
//   }

//   function minimax(board, depth, isMaximizing) {
//     const winnerInfo = calculateWinner(board);
//     if (winnerInfo) {
//       return winnerInfo.winner === 'O' ? 10 - depth : depth - 10;
//     }
//     if (!board.includes(null)) return 0;

//     if (isMaximizing) {
//       let bestScore = -Infinity;
//       for (let i = 0; i < 9; i++) {
//         if (board[i] === null) {
//           board[i] = 'O';
//           bestScore = Math.max(bestScore, minimax(board, depth + 1, false));
//           board[i] = null;
//         }
//       }
//       return bestScore;
//     } else {
//       let bestScore = Infinity;
//       for (let i = 0; i < 9; i++) {
//         if (board[i] === null) {
//           board[i] = 'X';
//           bestScore = Math.min(bestScore, minimax(board, depth + 1, true));
//           board[i] = null;
//         }
//       }
//       return bestScore;
//     }
//   }

//   function findHardAIMove(board) {
//     let bestScore = -Infinity, move = -1;
//     for (let i = 0; i < 9; i++) {
//       if (board[i] === null) {
//         board[i] = 'O';
//         let score = minimax(board, 0, false);
//         board[i] = null;
//         if (score > bestScore) {
//           bestScore = score;
//           move = i;
//         }
//       }
//     }
//     return move;
//   }

//   function findEasyAIMove(board) {
//     const moves = board.map((v, i) => v === null ? i : null).filter(v => v !== null);
//     return moves.length > 0 ? moves[Math.floor(Math.random() * moves.length)] : -1;
//   }

//   return (
//     <div className="tictoc">
//       <h1 className="title">🎯 Tic Tac Toe 🎮</h1>
//       <div className={`connection-status ${connectionStatus}`}>
//         Status: {connectionStatus}
//       </div>

//       <div className="mode-select">
//         <button
//           onClick={() => { setGameMode('humanVsHuman'); newGame(); }}
//           className={gameMode === 'humanVsHuman' ? 'active' : ''}
//           disabled={activeGameRoom !== null}
//         >
//           👥 Human vs Human
//         </button>
//         <button
//           onClick={() => { setGameMode('humanVsAI'); newGame(); }}
//           className={gameMode === 'humanVsAI' ? 'active' : ''}
//           disabled={activeGameRoom !== null}
//         >
//           🤖 Human vs AI
//         </button>
//         {currentUserRole === 'student' && (
//           <button
//             onClick={() => { setGameMode('studentVsTeacher'); newGame(); fetchInvitations(); }}
//             className={gameMode === 'studentVsTeacher' ? 'active' : ''}
//             disabled={activeGameRoom !== null}
//           >
//             🧑‍🎓 Student vs Teacher 🧑‍🏫
//           </button>
//         )}
//       </div>

//       {gameMode === 'humanVsAI' && (
//         <div className="difficulty-select">
//           <button
//             onClick={() => { setAiDifficulty('easy'); newGame(); }}
//             className={aiDifficulty === 'easy' ? 'active' : ''}
//           >
//             😄 Easy
//           </button>
//           <button
//             onClick={() => { setAiDifficulty('hard'); newGame(); }}
//             className={aiDifficulty === 'hard' ? 'active' : ''}
//           >
//             🔥 Hard
//           </button>
//         </div>
//       )}

//       {gameMode === 'studentVsTeacher' && currentUserRole === 'student' && (
//         <div className="student-teacher-section">
//           <h2>Your Invitations</h2>
//           {invitations.length === 0 && !activeGameRoom && !waitingForOpponent && <p>No pending invitations.</p>}
//           {waitingForOpponent && <p>Waiting for {opponentName || 'teacher'} to start the game...</p>}

//           {invitations.map(inv => (
//             <div key={inv._id} className="invitation-card">
//               <p>From: <strong>{inv.teacher.name}</strong></p>
//               <button onClick={() => handleInvitationResponse(inv._id, true)} disabled={waitingForOpponent}>Accept</button>
//               <button onClick={() => handleInvitationResponse(inv._id, false)} disabled={waitingForOpponent}>Reject</button>
//             </div>
//           ))}

//           {activeGameRoom && (
//             <div className="current-game-info">
//               <h3>Playing against: {opponentName} (You are {playerSymbol})</h3>
//             </div>
//           )}
//         </div>
//       )}

//       <div className="board">
//         {board.map((value, i) => (
//           <Square key={i} value={value} onClick={() => handleClick(i)} isWinningSquare={winningLine?.includes(i)} />
//         ))}
//       </div>

//       <div className="status">
//         {status}
//         {gameMode === 'studentVsTeacher' && activeGameRoom && playerSymbol && (
//           <span> (You are {playerSymbol})</span>
//         )}
//       </div>

//       <div className="scores">
//         <div>❌ X: {scoreX}</div>
//         <div>🤝 Draw: {draws}</div>
//         <div>⭕ O: {scoreO}</div>
//       </div>

//       <div className="controls">
//         {gameOver ? (
//           <button onClick={resetBoard}>Play Again 🔄</button>
//         ) : (
//           <button onClick={newGame}>New Game 🆕</button>
//         )}
//       </div>
//     </div>
//   );
// }

// export default TicTacToe;